// The report pane of the verification view (TGCLOUD-1868, plan D1): the lab report itself, drawn
// with pdf.js, with a box over every value the reader found in it.
//
// WHY pdf.js, AND NOT THE BROWSER'S OWN VIEWER. The 1866 review showed the report in an <iframe>
// (lab-report-inbox/ReportPreview.jsx), which cannot scroll to a place in the report, cannot draw
// over it, and cannot say whether it drew anything. Checking a value against the report needs all
// three. This pane draws each page to a canvas, puts a box over the words each value was read from,
// scrolls to a value when the Test pane asks, and says when the report could not be shown, so the
// view can mark every value as unverifiable (plan D5).
//
// THE PROPS
//   document        { kind: 'blob', blob }: a report uploaded in this browser; or
//                   { kind: 'fetch', fetchBytes }: the stored copy (an inbox hand-off, a saved
//                   Test). fetchBytes is called with { signal } and answers an ArrayBuffer, a typed
//                   array or a Blob. The document is loaded again only when its blob, its
//                   fetchBytes or its optional `key` changes, so give a key when fetchBytes is a
//                   new function on every render.
//   anchors         [{ path, page, rects: [{ x, y, w, h }], label?, tone?, tag? }], in PDF user
//                   space (plan A1; anchorGeometry.js). `label` names the field for a screen reader;
//                   without one it is named from its path (anchorLabels.js). `tone` draws the box
//                   as a kind of its own: 'other-individual', another person's row of a report
//                   about several people, read for one of them (TGCLOUD-1868, Phase E4), is hatched
//                   and dashed, so it reads at a glance as not this Test's. `tag` is words drawn on
//                   the report at the anchor's first box ("Another individual’s result: not this
//                   Test"), for everyone, not only a screen reader.
//   selectedPaths   the paths drawn strongly. Every other anchor is drawn faintly.
//   onSelectAnchor  (paths): a box was clicked. `paths` is every path with a box under the click,
//                   in the anchors' order (two values read from the same words share a box). The
//                   pane selects nothing itself: selectedPaths does.
//   scrollRequest   { path, nonce }: scroll to that path's first box and flash it. A new nonce
//                   asks again for the same path.
//   onRendered      ({ pageCount }): once per document, when its first page is on screen.
//   onRenderFailed  (error): once per document, for any failure: the file, the library, the
//                   worker, the document, a page, or a decoder a page needed, and a report not on
//                   screen within `loadTimeoutMs` (LOAD_TIMEOUT_MS): a stalled worker or a request
//                   that never answers would otherwise spin for ever while every value read "Show
//                   in the report" (review of the view; hazard 1868-H7). "Try again" loads the
//                   document again, and onRendered follows if that succeeds.
//   readOnly        the saved Test's view (plan D6). Nothing in the pane differs yet.
//
// The pane fills its parent's height and scrolls inside itself, so the Test beside it keeps its
// place. Give it a parent with a height.
//
// LAZY, BUT NEVER BLANK WHERE A BOX IS. Every page is laid out at its own size from the start,
// boxes included, so a scroll request reaches a page that has not been drawn yet. A page is drawn
// when it comes within a screen of the view (IntersectionObserver; every page, where there is
// none), at the pane's width times the device's pixel ratio, and again when the zoom or the pane's
// width changes (a resize once it settles). It is drawn into a new canvas that replaces the old one
// only once complete, so a zoom never flashes white.
//
// COLOURS ARE THE THEME'S, AND NONE IS CLINICAL (src/theme/palette.js). A box is the theme's
// INFORMATION blue (palette.info, the blue of every info alert in the app) at a low opacity, so the
// words read through it, and stronger when selected: it says where a value was read, nothing more.
// Not the gold of an unsaved change, which on the Test beside it marks an edit (Tim, 2026-10-04,
// replacing a neutral grey that did not stand out). Not the primary colour either, which is each
// group's own button colour: by default a pink close to the pathogenic red, and it can be the brand
// teal, which is the Phenotype record type. palette.info is not in the clinical register; the
// nearest blue there, Treatment's, marks a record type on record tiles, never on a report. Another
// person's row is told apart by its pattern (hatched, a dashed edge) and its words, in a neutral: no
// colour of its own, and none that could be taken for a clinical one.
//
// WHEN IT GOES. On unmount, or when the document changes, every render is cancelled, the document
// is destroyed, its worker is terminated and any object URL is revoked.
import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  MAX_SCALE,
  MIN_SCALE,
  anchorsByPage,
  boxCentre,
  boxContains,
  boxKeyOf,
  boxesForPage,
  firstBoxOf,
  fitWidthScale,
  outputScaleFor,
  pageAt,
  pageTops,
  pathsAtPoint,
  stepZoom,
  zoomedScale
} from './anchorGeometry';
import { ariaLabelOf, countsOf } from './anchorLabels';
import { loadPdfJs, startPdfWorker } from './pdfjsLoader';
import { OTHER_INDIVIDUAL_TONE } from './whichIndividual';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const EMPTY = Object.freeze([]);
/** CSS pixels between pages, and above the first. */
export const PAGE_GAP = 16;
/** CSS pixels either side of the widest page at fit to width. */
export const PAGE_GUTTER = 16;
/** How long the pane's width must hold still before the pages are drawn again for it. */
export const RESIZE_SETTLE_MS = 150;
/** How long a box asked for by a scroll request flashes. */
export const FLASH_MS = 1600;
/** How long pdf.js may take to finish with a destroyed document before its worker is terminated. */
export const WORKER_GRACE_MS = 1000;

// How long a report has to reach the screen, from asking for it to its first page drawn, before
// the pane says it could not be shown.
export const LOAD_TIMEOUT_MS = 45000;
const SHOWN_FAILED = 'The report could not be shown here.';
const TOOK_TOO_LONG = 'The report took too long to show.';

// ── Small helpers ──────────────────────────────────────────────────────────────────────────────

// The latest props, for callbacks that must keep one identity (a page's drawing depends on them).
function useLatest(value) {
  const ref = React.useRef(value);
  React.useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

const asError = (reason, fallback) =>
  reason instanceof Error
    ? reason
    : new Error((reason && reason.message) || fallback);

// pdf.js rejects a cancelled render, and a destroyed document's requests, with these.
const isCancellation = (error) =>
  !!error &&
  (error.name === 'RenderingCancelledException' ||
    error.name === 'AbortException');

const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

const asPdfBlob = (blob) =>
  blob.type === 'application/pdf'
    ? blob
    : new Blob([blob], { type: 'application/pdf' });

/**
 * The report as a PDF Blob, whichever way it came. A Blob holds its own copy of the bytes: pdf.js
 * TRANSFERS the buffer it is given to its worker, which empties it, so the caller's buffer must
 * never be the one handed over.
 */
async function reportBlobOf(source, signal) {
  if (source.kind === 'blob' && isBlob(source.blob)) {
    return asPdfBlob(source.blob);
  }
  if (source.kind === 'fetch' && typeof source.fetchBytes === 'function') {
    const bytes = await source.fetchBytes({ signal });
    if (isBlob(bytes)) return asPdfBlob(bytes);
    if (bytes instanceof ArrayBuffer || ArrayBuffer.isView(bytes)) {
      return new Blob([bytes], { type: 'application/pdf' });
    }
    throw new Error('The report file could not be read.');
  }
  throw new Error('No report file was given.');
}

// A Blob's bytes. jsdom's Blob has no arrayBuffer(); FileReader does the same job.
function bytesOfBlob(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(reader.error || new Error('The report file could not be read.'));
    reader.readAsArrayBuffer(blob);
  });
}

// Destroy a document, then terminate its worker once pdf.js has finished with it (or after
// WORKER_GRACE_MS, if the worker never answers). Safe to call twice.
function release(handles) {
  const { loadingTask, pdfWorker, webWorker, onWorkerError } = handles;
  handles.loadingTask = null;
  handles.pdfWorker = null;
  handles.webWorker = null;
  let finished = false;
  let grace = null;
  const finish = () => {
    if (finished) return;
    finished = true;
    clearTimeout(grace);
    try {
      if (pdfWorker) pdfWorker.destroy();
    } catch (e) {
      /* already destroyed */
    }
    if (webWorker) {
      if (onWorkerError && webWorker.removeEventListener) {
        webWorker.removeEventListener('error', onWorkerError);
        webWorker.removeEventListener('messageerror', onWorkerError);
      }
      try {
        webWorker.terminate();
      } catch (e) {
        /* already terminated */
      }
    }
  };
  if (!loadingTask) {
    finish();
    return;
  }
  let destroyed = null;
  try {
    destroyed = loadingTask.destroy();
  } catch (e) {
    destroyed = null;
  }
  grace = setTimeout(finish, WORKER_GRACE_MS);
  Promise.resolve(destroyed).then(finish, finish);
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── One box ────────────────────────────────────────────────────────────────────────────────────

const flash = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const AnchorBox = styled('button')(({ theme }) => {
  const neutral = theme.palette.common.black;
  const accent = theme.palette.info.main;
  return {
    position: 'absolute',
    display: 'block',
    margin: 0,
    padding: 0,
    border: 0,
    borderRadius: 2,
    font: 'inherit',
    cursor: 'pointer',
    zIndex: 1,
    backgroundColor: alpha(accent, 0.1),
    boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.55)}`,
    // Another person's row: hatched, with a dashed edge. Before hover, focus and selection, so each
    // of them still draws over it.
    [`&[data-tone="${OTHER_INDIVIDUAL_TONE}"]`]: {
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      backgroundImage: `repeating-linear-gradient(135deg, ${alpha(
        neutral,
        0.16
      )} 0 3px, transparent 3px 7px)`,
      border: `1px dashed ${alpha(neutral, 0.6)}`,
      boxShadow: 'none'
    },
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: alpha(accent, 0.16),
        boxShadow: `inset 0 0 0 1px ${accent}`
      }
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.info.dark}`,
      outlineOffset: 2,
      zIndex: 3
    },
    // Selected: no darker inside than a hover, with its stronger edge drawn OUTSIDE the box, so a
    // small value (a date) still reads through it. An edge inside the box ran over the words.
    '&[data-selected="true"]': {
      zIndex: 2,
      backgroundColor: alpha(accent, 0.16),
      boxShadow: `inset 0 0 0 1px ${accent}, 0 0 0 2px ${alpha(accent, 0.5)}`
    },
    // The flash is a ring around the box, so the words inside stay readable while it pulses.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -4,
      borderRadius: 4,
      border: `3px solid ${accent}`,
      opacity: 0,
      pointerEvents: 'none'
    },
    '&[data-flash="true"]': { zIndex: 3 },
    '&[data-flash="true"]::after': {
      animation: `${flash} 0.8s ease-in-out 2`
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&[data-flash="true"]::after': { animation: 'none', opacity: 1 }
    }
  };
});

// An anchor's words on the report, just above its first box: drawn for everyone, never in the way
// of a click.
const AnchorTag = styled('span')(({ theme }) => ({
  position: 'absolute',
  zIndex: 4,
  transform: 'translateY(calc(-100% - 2px))',
  padding: '0 4px',
  borderRadius: 2,
  fontSize: 10,
  lineHeight: '14px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  border: `1px dashed ${alpha(theme.palette.common.black, 0.6)}`
}));

// ── One page ───────────────────────────────────────────────────────────────────────────────────

const PdfPage = React.memo(function PdfPage({
  page,
  pageCount,
  scale,
  near,
  pageAnchors,
  labels,
  selected,
  flashPath,
  onSelect,
  onDrawn,
  onFailed
}) {
  const t = useAutoTranslation();

  const pageRef = React.useRef(null);
  const holderRef = React.useRef(null);
  const drawnRef = React.useRef(null);
  const viewport = React.useMemo(
    () => page.proxy.getViewport({ scale }),
    [page.proxy, scale]
  );
  const boxes = React.useMemo(
    () => boxesForPage(pageAnchors, viewport),
    [pageAnchors, viewport]
  );

  // Draw the page when it is near the view and not already drawn at this scale.
  React.useEffect(() => {
    if (!near) return undefined;
    const ratio =
      typeof window !== 'undefined' && window.devicePixelRatio > 0
        ? window.devicePixelRatio
        : 1;
    const output = outputScaleFor(viewport.width, viewport.height, ratio);
    const drawing = `${viewport.width}x${viewport.height}@${output}`;
    if (drawnRef.current === drawing) return undefined;
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width * output));
    canvas.height = Math.max(1, Math.floor(viewport.height * output));
    const sx = canvas.width / viewport.width;
    const sy = canvas.height / viewport.height;
    let live = true;
    let settled = false;
    let task = null;
    try {
      task = page.proxy.render({
        canvas,
        viewport,
        transform: sx === 1 && sy === 1 ? null : [sx, 0, 0, sy, 0, 0]
      });
    } catch (error) {
      onFailed(asError(error, 'A page of the report could not be drawn.'));
      return undefined;
    }
    task.promise.then(
      () => {
        settled = true;
        if (!live) return;
        drawnRef.current = drawing;
        const holder = holderRef.current;
        if (holder) {
          holder.querySelectorAll('canvas').forEach((old) => {
            old.width = 0;
            old.height = 0;
          });
          holder.replaceChildren(canvas);
        }
        onDrawn(page.number);
      },
      (error) => {
        settled = true;
        if (!live || isCancellation(error)) return;
        onFailed(asError(error, 'A page of the report could not be drawn.'));
      }
    );
    return () => {
      live = false;
      // Only a drawing still in progress is cancelled: pdf.js runs a finished task's completion
      // again when it is cancelled.
      if (settled) return;
      try {
        task.cancel();
      } catch (e) {
        /* already finished */
      }
    };
  }, [near, page, viewport, onDrawn, onFailed]);

  // A drawn canvas holds memory until it is emptied, in Safari most of all.
  React.useEffect(() => {
    const holder = holderRef.current;
    return () => {
      if (!holder) return;
      holder.querySelectorAll('canvas').forEach((canvas) => {
        canvas.width = 0;
        canvas.height = 0;
      });
    };
  }, []);

  // A click selects every path with a box under it. From the keyboard there is no pointer, so the
  // box pressed is taken to be clicked in its middle.
  const choose = (event, box) => {
    let point = null;
    if (event.detail > 0 && pageRef.current) {
      const at = pageRef.current.getBoundingClientRect();
      point = { x: event.clientX - at.left, y: event.clientY - at.top };
    }
    if (!point || !boxContains(box.box, point)) point = boxCentre(box.box);
    onSelect(pathsAtPoint(boxes, point));
  };

  return (
    <Box
      ref={pageRef}
      data-page-number={page.number}
      role="group"
      aria-label={t('Page {{number}} of {{pageCount}}', {
        number: page.number,
        pageCount
      })}
      style={{ width: viewport.width, height: viewport.height }}
      sx={{
        position: 'relative',
        flex: 'none',
        mx: 'auto',
        mb: `${PAGE_GAP}px`,
        bgcolor: 'common.white',
        boxShadow: 2
      }}
    >
      <Box
        ref={holderRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          '& > canvas': { display: 'block', width: '100%', height: '100%' }
        }}
      />
      {boxes.map((b) => {
        const isSelected = selected.has(b.path);
        const common = {
          'data-anchor-key': b.key,
          'data-anchor-path': b.path,
          'data-tone': b.tone || undefined,
          'data-selected': isSelected ? 'true' : undefined,
          'data-flash': flashPath === b.path ? 'true' : undefined,
          style: {
            left: b.box.left,
            top: b.box.top,
            width: b.box.width,
            height: b.box.height
          },
          onClick: (event) => choose(event, b)
        };
        // A value printed over several lines has a box per line and one button, its first box: one
        // place in the tab order and one name for a screen reader. The other boxes are only there
        // to be clicked.
        return b.first ? (
          <AnchorBox
            key={b.key}
            type="button"
            aria-label={labels.get(b.anchorIndex)}
            aria-pressed={isSelected}
            {...common}
          />
        ) : (
          <AnchorBox key={b.key} as="span" aria-hidden="true" {...common} />
        );
      })}
      {/* Its words on the report, at each anchor's first box (its label says them to a screen
          reader, so these are hidden from one). */}
      {boxes
        .filter((b) => b.first && b.tag)
        .map((b) => (
          <AnchorTag
            key={`tag:${b.key}`}
            aria-hidden="true"
            data-anchor-tag={b.path}
            style={{ left: b.box.left, top: b.box.top }}
          >
            {b.tag}
          </AnchorTag>
        ))}
    </Box>
  );
});

// ── The pane ───────────────────────────────────────────────────────────────────────────────────

const IDLE = Object.freeze({
  status: 'idle',
  pages: EMPTY,
  blob: null,
  error: null
});

export default function ReportViewer({
  document: source,
  anchors = EMPTY,
  selectedPaths = EMPTY,
  onSelectAnchor,
  scrollRequest,
  onRendered,
  onRenderFailed,
  readOnly = false,
  loadTimeoutMs = LOAD_TIMEOUT_MS
}) {
  const t = useAutoTranslation();

  const scrollRef = React.useRef(null);
  const timeoutRef = React.useRef(loadTimeoutMs);
  timeoutRef.current = loadTimeoutMs;
  const [view, setView] = React.useState(IDLE);
  const [attempt, setAttempt] = React.useState(0);
  const [paneWidth, setPaneWidth] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const [near, setNear] = React.useState(() => new Set());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [flashing, setFlashing] = React.useState(null);
  const [blobUrl, setBlobUrl] = React.useState(null);

  const latest = useLatest({ onSelectAnchor, onRendered, onRenderFailed });
  const sourceRef = useLatest(source);
  const handlesRef = React.useRef(null);
  const reportedRef = React.useRef({ rendered: false, failed: false });
  const pageCountRef = React.useRef(0);
  const scrollRatioRef = React.useRef(0);
  const handledRequestRef = React.useRef(undefined);

  // The first failure of a document is reported, and the pane shows that it could not show it.
  const fail = React.useCallback(
    (error) => {
      const reported = reportedRef.current;
      if (reported.failed) return;
      reported.failed = true;
      if (handlesRef.current) release(handlesRef.current);
      setView((v) => ({ ...v, status: 'failed', pages: EMPTY, error }));
      latest.current.onRenderFailed?.(error);
    },
    [latest]
  );

  const drawn = React.useCallback(() => {
    const reported = reportedRef.current;
    if (reported.rendered || reported.failed) return;
    reported.rendered = true;
    latest.current.onRendered?.({ pageCount: pageCountRef.current });
  }, [latest]);

  const select = React.useCallback(
    (paths) => latest.current.onSelectAnchor?.(paths),
    [latest]
  );

  // ── Loading the document ─────────────────────────────────────────────────────────────────────

  const kind = source ? source.kind : null;
  const identity = !source
    ? null
    : source.key !== undefined && source.key !== null
    ? source.key
    : source.kind === 'blob'
    ? source.blob
    : source.fetchBytes;

  React.useEffect(() => {
    const given = sourceRef.current;
    const reported = { rendered: false, failed: false };
    reportedRef.current = reported;
    pageCountRef.current = 0;
    if (!given) {
      setView(IDLE);
      return undefined;
    }
    let alive = true;
    // Gone, or failed already: a step that finishes after either must change nothing. A failure
    // can arrive mid-load (the worker's error event), and the pages that follow it must not
    // replace the failure with a report drawn from a destroyed document.
    const stopped = () => !alive || reported.failed;
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const handles = {
      loadingTask: null,
      pdfWorker: null,
      webWorker: null,
      onWorkerError: null
    };
    handlesRef.current = handles;
    setView({ status: 'loading', pages: EMPTY, blob: null, error: null });
    setZoom(1);
    setNear(new Set());
    setCurrentPage(1);

    const failed = (reason) => {
      if (alive) fail(asError(reason, SHOWN_FAILED));
    };
    // Not on screen in time: the report could not be shown, whatever step it waits on.
    const limit = timeoutRef.current;
    const timer =
      Number.isFinite(limit) && limit > 0
        ? setTimeout(() => {
            if (!reported.rendered && !reported.failed) {
              failed(new Error(TOOK_TOO_LONG));
            }
          }, limit)
        : null;

    (async () => {
      const blob = await reportBlobOf(given, controller && controller.signal);
      if (stopped()) return;
      setView((v) => ({ ...v, blob }));
      const { pdfjs, binaryDataFactoryFor } = await loadPdfJs();
      if (stopped()) return;
      const data = new Uint8Array(await bytesOfBlob(blob));
      if (stopped()) return;
      const webWorker = await startPdfWorker();
      if (stopped()) {
        webWorker.terminate();
        return;
      }
      handles.webWorker = webWorker;
      handles.onWorkerError = (event) =>
        failed(
          new Error(
            (event && event.message) || 'The report reader stopped working.'
          )
        );
      webWorker.addEventListener('error', handles.onWorkerError);
      webWorker.addEventListener('messageerror', handles.onWorkerError);
      handles.pdfWorker = new pdfjs.PDFWorker({ port: webWorker });
      handles.loadingTask = pdfjs.getDocument({
        data,
        worker: handles.pdfWorker,
        BinaryDataFactory: binaryDataFactoryFor({
          onMissing: (what, error) =>
            failed(
              new Error(
                `Part of the report could not be drawn (${what}): ${error.message}`
              )
            )
        }),
        useWorkerFetch: false,
        enableXfa: false,
        verbosity:
          pdfjs.VerbosityLevel && Number.isInteger(pdfjs.VerbosityLevel.ERRORS)
            ? pdfjs.VerbosityLevel.ERRORS
            : 0
      });
      const pdf = await handles.loadingTask.promise;
      if (stopped()) return;
      if (!(pdf.numPages > 0)) throw new Error('The report has no pages.');
      const proxies = await Promise.all(
        Array.from({ length: pdf.numPages }, (_, i) => pdf.getPage(i + 1))
      );
      if (stopped()) return;
      const pages = proxies.map((proxy, i) => {
        const { width, height } = proxy.getViewport({ scale: 1 });
        return { number: i + 1, proxy, width, height };
      });
      pageCountRef.current = pages.length;
      setView({ status: 'ready', pages, blob, error: null });
    })().catch(failed);

    return () => {
      alive = false;
      clearTimeout(timer);
      if (controller) controller.abort();
      release(handles);
      if (handlesRef.current === handles) handlesRef.current = null;
    };
    // The document is identified by its kind and identity, not by the object it arrives in, so a
    // parent that passes a new { kind, blob } on each render does not reload it.
  }, [kind, identity, attempt, fail, sourceRef]);

  // "Open in a new tab", once the pane has failed but holds the file: another way to read it.
  React.useEffect(() => {
    if (view.status !== 'failed' || !view.blob) return undefined;
    if (typeof URL === 'undefined' || !URL.createObjectURL) return undefined;
    const url = URL.createObjectURL(view.blob);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [view.status, view.blob]);

  // ── The pane's width, and the scale it gives ─────────────────────────────────────────────────

  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () =>
      setPaneWidth((was) =>
        Math.abs(el.clientWidth - was) >= 1 ? el.clientWidth : was
      );
    measure();
    let timer = null;
    const settle = () => {
      clearTimeout(timer);
      timer = setTimeout(measure, RESIZE_SETTLE_MS);
    };
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(settle);
      observer.observe(el);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', settle);
    }
    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
      else if (typeof window !== 'undefined') {
        window.removeEventListener('resize', settle);
      }
    };
  }, []);

  const { pages } = view;
  const fitScale = React.useMemo(
    () =>
      fitWidthScale(
        paneWidth - 2 * PAGE_GUTTER,
        pages.map((p) => p.width)
      ),
    [paneWidth, pages]
  );
  const scale = zoomedScale(fitScale, zoom);
  const heights = React.useMemo(
    () => pages.map((p) => p.height * scale),
    [pages, scale]
  );
  const tops = React.useMemo(() => pageTops(heights, PAGE_GAP), [heights]);

  // Keep the same place in the report when the scale changes, by zoom or by resize.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !(el.scrollHeight > 0)) return;
    el.scrollTop = scrollRatioRef.current * el.scrollHeight;
  }, [scale]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight > 0)
      scrollRatioRef.current = el.scrollTop / el.scrollHeight;
    const atBottom =
      el.scrollHeight > 0 &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setCurrentPage(
      atBottom
        ? pages.length
        : pageAt(tops, heights, el.scrollTop + el.clientHeight / 2) || 1
    );
  };

  // ── Which pages are near the view ────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (view.status !== 'ready') return undefined;
    const root = scrollRef.current;
    const all = () => new Set(pages.map((p) => p.number));
    if (!root || typeof IntersectionObserver === 'undefined') {
      setNear(all());
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        setNear((was) => {
          const next = new Set(was);
          for (const entry of entries) {
            const n = Number(entry.target.getAttribute('data-page-number'));
            if (entry.isIntersecting) next.add(n);
            else next.delete(n);
          }
          const same =
            next.size === was.size && [...next].every((n) => was.has(n));
          return same ? was : next;
        });
      },
      { root, rootMargin: '100% 0px', threshold: 0 }
    );
    root
      .querySelectorAll('[data-page-number]')
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [view.status, pages]);

  // ── The boxes ────────────────────────────────────────────────────────────────────────────────

  const byPage = React.useMemo(
    () => anchorsByPage(anchors, pages.length || undefined),
    [anchors, pages.length]
  );
  const labels = React.useMemo(() => {
    const counts = countsOf(anchors);
    const out = new Map();
    (Array.isArray(anchors) ? anchors : []).forEach((anchor, i) => {
      out.set(i, ariaLabelOf(anchor, counts, t));
    });
    return out;
  }, [anchors, t]);
  const selected = React.useMemo(
    () => new Set(Array.isArray(selectedPaths) ? selectedPaths : EMPTY),
    [selectedPaths]
  );

  // A scroll request is honoured once per nonce, against the anchors, once the document is laid
  // out. One made while the report loads waits for it; one already honoured is not honoured again
  // when the document changes, since it was asked of the last one.
  const requestId =
    scrollRequest && 'nonce' in scrollRequest
      ? scrollRequest.nonce
      : scrollRequest;
  React.useEffect(() => {
    if (!scrollRequest || view.status !== 'ready') return;
    if (handledRequestRef.current === requestId) return;
    handledRequestRef.current = requestId;
    const first = firstBoxOf(byPage, scrollRequest.path);
    if (!first) return;
    const target = scrollRef.current?.querySelector(
      `[data-anchor-key="${boxKeyOf(first.anchorIndex, first.rectIndex)}"]`
    );
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }
    setFlashing({ path: scrollRequest.path, id: requestId });
  }, [scrollRequest, requestId, view.status, byPage]);

  React.useEffect(() => {
    if (!flashing) return undefined;
    const timer = setTimeout(() => setFlashing(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashing]);

  // ── Zoom ─────────────────────────────────────────────────────────────────────────────────────

  const zoomIn = () => setZoom((z) => stepZoom(z, fitScale, 1));
  const zoomOut = () => setZoom((z) => stepZoom(z, fitScale, -1));
  const fitToWidth = () => setZoom(1);
  const percent = Math.round(scale * 100);

  // ── Drawing ──────────────────────────────────────────────────────────────────────────────────

  const ready = view.status === 'ready';
  return (
    <Box
      data-testid="report-viewer"
      data-read-only={readOnly ? 'true' : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0
      }}
    >
      {ready && (
        <Box
          role="toolbar"
          aria-label={t('Report view')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
            flex: 'none'
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            {t('Page {{currentPage}} of {{count}}', {
              currentPage: Math.min(currentPage, pages.length),
              count: pages.length
            })}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('Zoom out')}
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE + 1e-9}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            data-testid="report-zoom"
            sx={{ minWidth: 44, textAlign: 'center' }}
          >
            {`${percent}%`}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('Zoom in')}
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE - 1e-9}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            startIcon={<FitScreenIcon fontSize="small" />}
            onClick={fitToWidth}
          >
            {t('Fit to width')}
          </Button>
        </Box>
      )}
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        // Focusable, so the keyboard can scroll the report even where it has no box to reach.
        role="region"
        aria-label={t('The lab report')}
        tabIndex={0}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          bgcolor: 'action.hover',
          px: `${PAGE_GUTTER}px`,
          pt: `${PAGE_GAP}px`,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2
          }
        }}
      >
        {view.status === 'loading' && (
          <Box
            sx={{ p: 4, textAlign: 'center' }}
            role="status"
            aria-label={t('Loading the report')}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        {view.status === 'idle' && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t('No report to show.')}
          </Typography>
        )}
        {view.status === 'failed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(SHOWN_FAILED)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setAttempt((n) => n + 1)}
              >
                {t('Try again')}
              </Button>
              {blobUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  href={blobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Open in a new tab')}
                </Button>
              )}
            </Box>
          </Alert>
        )}
        {ready &&
          pages.map((page) => (
            <PdfPage
              key={page.number}
              page={page}
              pageCount={pages.length}
              scale={scale}
              near={near.has(page.number)}
              pageAnchors={byPage.get(page.number) || EMPTY}
              labels={labels}
              selected={selected}
              flashPath={flashing ? flashing.path : null}
              onSelect={select}
              onDrawn={drawn}
              onFailed={fail}
            />
          ))}
      </Box>
    </Box>
  );
}
